import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { AuthPanel } from "@/components/rc/AuthForms";
import { Overview } from "@/components/rc/Overview";
import { EhcpTracker } from "@/components/rc/EhcpTracker";
import { Evidence } from "@/components/rc/Evidence";
import { ToolkitsPanel } from "@/components/rc/ToolkitsPanel";
import { Team } from "@/components/rc/Team";
import { Security } from "@/components/rc/Security";
import { Report } from "@/components/rc/Report";
import { ActivityLog } from "@/components/rc/ActivityLog";
import { useWorkspace, isManager } from "@/context/WorkspaceContext";

const EMPTY = { ehcp: [], evidence: [], saves: {}, members: [] };
const list = (r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : []);

const useWsData = () => {
  const [data, setData] = useState(EMPTY);
  const load = useCallback(async () => {
    const [ehcp, evidence, saves, members] = await Promise.allSettled(["/rc/ehcp", "/rc/evidence", "/rc/saves", "/rc/members"].map((u) => api.get(u)));
    setData({ ehcp: list(ehcp), evidence: list(evidence), members: list(members), saves: saves.status === "fulfilled" && saves.value?.data ? saves.value.data : {} });
  }, []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const indexFor = (manage) => {
  const items = [["ws-overview", "Overview"], ["ws-ehcp", "EHCP tracker"], ["ws-evidence", "Evidence"], ["ws-toolkits", "Toolkits"], ["ws-team", "Team"],
    ["ws-security", "Security"], ...(manage ? [["ws-activity", "Activity log"]] : []), ["ws-report", "Report"]].map(([id, short]) => ({ id, short }));
  return { label: "Workspace", items, ids: items.map((i) => i.id) };
};

const Dashboard = ({ ws }) => {
  const { signOut, refresh } = useWorkspace();
  const [data, reload] = useWsData();
  const role = ws.user.role;
  const manage = isManager(role);
  const write = role !== "governor";
  return (
    <>
      <TopBar minimal index={indexFor(manage)} />
      <main id="main" tabIndex={-1} className="outline-none" data-testid="ws-dashboard">
        <div className="mx-auto max-w-[1200px] space-y-8 px-5 pb-8 pt-[150px] sm:px-8 lg:pt-[178px]">
          <div className="flex justify-end no-print">
            <button type="button" onClick={signOut} className="btn btn-ghost btn-sm" data-testid="ws-signout"><LogOut size={14} aria-hidden /> Sign out</button>
          </div>
          <Overview ws={ws} data={data} reload={reload} refresh={refresh} manage={manage} />
          <EhcpTracker records={data.ehcp} canEdit={write} canDelete={manage} reload={reload} />
          <Evidence items={data.evidence} canWrite={write} canDelete={(ev) => manage || ev.created_by_id === ws.user.id} reload={reload} />
          <ToolkitsPanel saves={data.saves} readOnly={!write} />
          <Team members={data.members} me={ws.user.id} manage={manage} reload={reload} />
          {manage && <Security enabled={ws.user.mfa_enabled} refresh={refresh} manage />}
          {!manage && <Security refresh={refresh} />}
          {manage && <ActivityLog version={data} />}
        </div>
        <Report ws={ws} data={data} />
      </main>
      <Footer />
    </>
  );
};

const Gate = () => (
  <>
    <TopBar minimal />
    <main id="main" tabIndex={-1} className="outline-none">
      <section className="mx-auto grid max-w-[1100px] gap-10 px-5 pb-20 pt-[150px] sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:pt-[178px]" aria-labelledby="gate-title">
        <div>
          <p className="eyebrow mb-4 flex items-center gap-2"><Lock size={14} aria-hidden /> SENDCO Resource Centre</p>
          <h1 id="gate-title" className="font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">Your school's <span className="italic text-brand">secure workspace</span></h1>
          <p className="mt-6 text-lg leading-relaxed text-ink-soft">Save toolkit answers and return to them on any device, track EHCP annual reviews, keep evidence in one place and download PDF reports.</p>
          <p className="mt-4 text-ink-soft">Don't need an account? The free toolkits still work without signing up, and your answers save in this browser.</p>
          <Link to="/quick-start" className="btn btn-ghost mt-6" data-testid="gate-free-toolkits">Use the free toolkits</Link>
        </div>
        <AuthPanel />
      </section>
    </main>
    <Footer />
  </>
);

export default function WorkspacePage() {
  const { ws } = useWorkspace();
  if (ws === null) return <p className="p-10 pt-40 text-center font-bold" role="status" data-testid="ws-loading">Loading your workspace…</p>;
  return ws ? <Dashboard ws={ws} /> : <Gate />;
}
